import { useAtom, useAtomValue } from 'jotai';
import { documentFilterState, MyInfoState } from '@/recoil/atom';
import {
  INIT_RECORD_STATE,
  INIT_TALK_FLOW_COUNT_PARAMS,
  INIT_WEB_DOCUMENT_FILTER_STATE,
} from '@/recoil/init_data';
import { useWorkEventTracker } from '@/hooks/gtm/useGTMEventTracker';
import { EventPageAreaName, WorkEventName } from '@/constant/gtm';
import {
  createTalkFlow,
  useGetMyTalkFlowFavoriteFilter,
  useGetNewTalkFlowList,
  useGetTalkFlowFavoriteFilter,
} from '@/hooks/apiHooks';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import useDocumentsData from './useDocumentsData';
import {
  DocumentTypeFilter,
  DocumentTypeFilterLabel,
  RecordMode,
} from '@/types/api';
import { filterToParams } from '@/components/Record/util/function';
import { TalkFlowModalRef } from '@/components/Record/TalkFLowModal';

const pageArea = EventPageAreaName.WORK_DOCUMENT;
type UseDocumentFiltersParams = {
  /** 녹음하며 새 문서 작성에 쓰는 녹음 시작 함수. 작성 버튼이 있는 웹 목록만 넘긴다 */
  startRecording?: (docId: string) => Promise<boolean>;
};

const useDocumentFilters = ({
  startRecording,
}: UseDocumentFiltersParams = {}) => {
  const router = useRouter();
  const favoriteId = useMemo(
    () => router.query.favoriteId as string | undefined,
    [router.query.favoriteId],
  );

  const myInfo = useAtomValue(MyInfoState);
  const { passportId, name } = myInfo;

  const [documentState, setDocumentState] = useAtom(documentFilterState);
  const selectedId = documentState.id || documentState.category;

  const workEventTracker = useWorkEventTracker(pageArea);

  const { data: favoriteData = [] } = useGetMyTalkFlowFavoriteFilter();

  const { data: favoriteLinkData } = useGetTalkFlowFavoriteFilter(
    favoriteId as string,
  );

  const { mutate, data: countData } = useGetNewTalkFlowList(
    INIT_TALK_FLOW_COUNT_PARAMS,
  );

  const documentCount = countData?.meta?.totalCount ?? 0;

  const { bookMarkCount } = useDocumentsData();

  const recordModal = useRef<TalkFlowModalRef>(null);

  // 문서 생성 응답 전 연속 클릭으로 빈 문서가 여러 개 생기지 않게 한다
  const isCreatingRef = useRef(false);

  const resetWithState = useCallback(
    (overrides = {}) => ({
      ...INIT_WEB_DOCUMENT_FILTER_STATE,
      ...overrides,
    }),
    [],
  );

  const handleFilterClick = useCallback(
    (filter: { value: string; title: string }) => {
      const { value, title } = filter;

      const filterStateMap: Record<string, object> = {
        bookMark: { isBookmarked: true, category: value },
        all: { category: value },
        write: {
          createdBy: [{ passportId, name }],
          category: value,
        },
        referrer: { isOnlyShared: 1, category: value },
      };

      const stateOverride = filterStateMap[value];
      if (stateOverride) {
        setDocumentState(resetWithState(stateOverride));
      }

      workEventTracker(WorkEventName.clickEvent, {
        area_name: 'Document 목록',
        contents: title,
        status: undefined,
        description: '문서 필터',
      });
    },
    [passportId, name, resetWithState, setDocumentState, workEventTracker],
  );

  const handleFavoriteClick = useCallback(
    (id: string) => {
      const tempFavoriteData = favoriteData.find((item) => item.id === id);
      if (!tempFavoriteData) return;

      const { params, name: filterName } = tempFavoriteData;

      setDocumentState({
        ...INIT_WEB_DOCUMENT_FILTER_STATE,
        ...params,
        keyword: params?.keyword || '',
        category: 'favoriteFilter',
        id,
        filterName,
      });
    },
    [favoriteData, setDocumentState],
  );

  const sendGTMEvent = useCallback(
    (contents: string) => {
      workEventTracker(WorkEventName.clickEvent, {
        area_name: 'Document 목록',
        contents,
        status: undefined,
        description: '문서 필터',
      });
    },
    [workEventTracker],
  );

  const onAfterClose = useCallback(() => {
    mutate();
  }, [mutate]);

  /**
   * 새 문서를 만들어 모달로 연다.
   * withRecording 이면 그 문서에 녹음까지 바로 시작하고,
   * asMeeting 이면 문서 안 '회의록 템플릿 불러오기'를 누른 상태(회의록 양식 + 참석자에 나)로 만든다.
   */
  const createNewDocument = useCallback(
    async ({
      withRecording,
      asMeeting = false,
    }: {
      withRecording: boolean;
      asMeeting?: boolean;
    }) => {
      if (isCreatingRef.current) return;
      isCreatingRef.current = true;
      try {
        const obj = filterToParams(
          asMeeting
            ? {
                ...INIT_RECORD_STATE,
                mode: RecordMode.Meeting,
                internal: [
                  {
                    passportId: myInfo.passportId,
                    name: myInfo.name,
                    departmentName: myInfo.departmentName,
                    positionName: myInfo.positionName,
                    divisionName: myInfo.divisionName,
                    profileImageUrl: myInfo.profileImageUrl,
                  },
                ],
              }
            : INIT_RECORD_STATE,
        );
        const data = await createTalkFlow(obj);
        const docId = data.data.talkFlowDocumentId;
        // 생성 응답 전에 화면을 떠났으면 모달이 없다 — 녹음 제어 UI 없이 녹음이 시작되지 않게 멈춘다
        if (!recordModal.current) return;
        recordModal.current.open(docId);
        if (withRecording) await startRecording?.(docId);
      } catch (e) {
        console.error(e);
      } finally {
        isCreatingRef.current = false;
      }

      workEventTracker(WorkEventName.clickEvent, {
        area_name: 'Document 목록',
        contents: !withRecording
          ? '새 문서 작성'
          : asMeeting
          ? '회의록으로 녹음 시작'
          : '녹음 시작',
        status: undefined,
        description: '새 문서 작성',
      });
    },
    [myInfo, startRecording, workEventTracker],
  );

  const handleNewDocumentClick = useCallback(
    () => createNewDocument({ withRecording: false }),
    [createNewDocument],
  );

  const handleNewDocumentWithRecordingClick = useCallback(
    () => createNewDocument({ withRecording: true }),
    [createNewDocument],
  );

  const handleNewMeetingWithRecordingClick = useCallback(
    () => createNewDocument({ withRecording: true, asMeeting: true }),
    [createNewDocument],
  );

  const handleDocumentTypeChange = useCallback(
    (type: DocumentTypeFilter) => {
      const isChecked = documentState.documentTypes.includes(type);
      const newChecked = !isChecked;

      setDocumentState({
        ...documentState,
        documentTypes: newChecked
          ? [...documentState.documentTypes, type]
          : documentState.documentTypes.filter((item) => item !== type),
      });

      workEventTracker(WorkEventName.clickEvent, {
        area_name: '양식',
        contents: DocumentTypeFilterLabel[type],
        status: newChecked,
        description: '품의 필터',
      });
    },
    [documentState, setDocumentState, workEventTracker],
  );

  useEffect(() => {
    if (!favoriteId) return;

    const documentFavoriteLinkData = favoriteData.find(
      (item) => item.id === favoriteId,
    );

    if (documentFavoriteLinkData?.params) {
      setDocumentState({
        ...INIT_WEB_DOCUMENT_FILTER_STATE,
        ...documentFavoriteLinkData.params,
        keyword: documentFavoriteLinkData.params.keyword || '',
        id: documentFavoriteLinkData.id,
        filterName: documentFavoriteLinkData.name,
        category: 'all',
      });
    } else if (favoriteLinkData?.params) {
      setDocumentState({
        ...INIT_WEB_DOCUMENT_FILTER_STATE,
        ...favoriteLinkData.params,
        keyword: favoriteLinkData.params.keyword || '',
        // id를 설정하지 않음 (공유받은 필터는 "전체 문서" 카테고리 활성화)
        filterName: favoriteLinkData.name,
        category: 'all',
      });
    }
  }, [favoriteData, favoriteId, favoriteLinkData, setDocumentState]);

  return {
    pageArea,
    documentState,
    setDocumentState,
    selectedId,
    favoriteData,
    countData,
    documentCount,
    bookMarkCount,
    recordModal,
    handleFilterClick,
    handleFavoriteClick,
    handleNewDocumentClick,
    handleNewDocumentWithRecordingClick,
    handleNewMeetingWithRecordingClick,
    sendGTMEvent,
    onAfterClose,
    handleDocumentTypeChange,
  };
};
export default useDocumentFilters;
